import React, { useEffect, useMemo, useState } from "react";
import { listDrivers, updateDriver } from "./api";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const chip = (text, bg) => (
  <span style={{
    background: bg, color: "#fff", padding: "2px 8px", borderRadius: 999,
    fontSize: 12, fontWeight: 600
  }}>{text}</span>
);

export default function DriversTable() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [page, setPage] = useState(1);
  const [per, setPer] = useState(10);
  const [data, setData] = useState({ items: [], total: 0, pages: 0, page: 1 });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const navigate = useNavigate();

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const r = await listDrivers({ q, status, vehicle_type: vehicle, page, per_page: per });
      setData(r);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [page, per]);

  const onSearch = (e) => {
    e.preventDefault();
    setPage(1);
    load();
  };

  const rows = useMemo(() => data.items || [], [data.items]);

  const doUpdate = async (id, patch) => {
    try {
      await updateDriver({ driver_id: id, ...patch });
      await load();
    } catch (e) {
      alert(e.message);
    }
  };

  function openDocuments(id) {
    navigate("/documentlist/" + id);
  }

  // ✅ EXPORT FUNCTION
  const exportToExcel = () => {
    if (!rows.length) {
      alert("No data to export");
      return;
    }

    const formattedData = rows.map((d) => ({
      ID: d.id,
      Name: d.name,
      Phone: d.phone,
      Vehicle: `${d.vehicle_type} - ${d.vehicle_number}`,
      Status: d.status,
      MainStatus: d.main_status,
      Online: d.online ? "Online" : "Offline",
      VerifiedDocs: d.doc_summary?.verified,
      PendingDocs: d.doc_summary?.pending,
      ExpiredDocs: d.doc_summary?.expired,
      RejectedDocs: d.doc_summary?.rejected,
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);

    // Optional column width
    worksheet["!cols"] = [
      { wch: 6 }, { wch: 20 }, { wch: 15 },
      { wch: 25 }, { wch: 15 }, { wch: 15 },
      { wch: 10 }, { wch: 12 }, { wch: 12 },
      { wch: 12 }, { wch: 12 }
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Drivers");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    const fileName = `drivers_${new Date().toISOString().slice(0,10)}.xlsx`;
    saveAs(blob, fileName);
  };

  return (
    <div>
      <h2>Drivers</h2>

      {/* 🔍 Filters */}
      <form onSubmit={onSearch} style={{ display: "flex", gap: 8, margin: "12px 0", flexWrap: "wrap" }}>
        <input placeholder="Search name/phone/vehicle #" value={q} onChange={(e) => setQ(e.target.value)} />
        
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Status</option>
          <option value="approved">approved</option>
          <option value="pending">pending</option>
          <option value="blocked">blocked</option>
        </select>

        <select value={vehicle} onChange={(e) => setVehicle(e.target.value)}>
          <option value="">Vehicle type</option>
          <option value="car">car</option>
          <option value="auto">auto</option>
          <option value="bike">bike</option>
        </select>

        <button type="submit">Search</button>

        {/* ✅ EXPORT BUTTON */}
        <button
          type="button"
          onClick={exportToExcel}
          style={{
            background: "#2e7d32",
            color: "#fff",
            padding: "6px 10px",
            border: "none",
            borderRadius: 4
          }}
        >
          Export Excel
        </button>

        {loading && <span>Loading…</span>}
        {err && <span style={{ color: "red" }}>{err}</span>}
      </form>

      {/* 📊 TABLE */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #eee" }}>
              <th>ID</th><th>Name</th><th>Phone</th><th>Vehicle</th>
              <th>Status</th><th>Main</th><th>Online</th><th>Docs</th><th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {rows.map(d => (
              <tr key={d.id} style={{ borderBottom: "1px solid #f3f3f3" }}>
                <td>{d.id}</td>
                <td>{d.name}</td>
                <td>{d.phone}</td>
                <td>{d.vehicle_type} • {d.vehicle_number}</td>
                <td>{chip(d.status || "-", "#1976d2")}</td>
                <td>{chip(d.main_status || "-", "#6d4c41")}</td>

                <td>
                  <input
                    type="checkbox"
                    checked={!!d.online}
                    onChange={(e) =>
                      doUpdate(d.id, { online: e.target.checked ? 1 : 0 })
                    }
                  />
                </td>

                <td>
                  <small>
                    v:{d.doc_summary.verified} p:{d.doc_summary.pending}
                  </small>
                </td>

                <td>
                  <button onClick={() => doUpdate(d.id, { status: "approved", main_status: "approved" })}>Approve</button>
                  <button onClick={() => doUpdate(d.id, { status: "blocked", main_status: "rejected" })}>Block</button>
                  <button onClick={() => openDocuments(d.id)}>Docs</button>
                </td>
              </tr>
            ))}

            {!rows.length && !loading && (
              <tr><td colSpan={9}>No drivers</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}